import type { Lang } from './i18n';

/**
 * Every endpoint this app depends on.
 *
 * `PATHS` is exported so `scripts/doctor.mjs` can verify each one against a
 * running API. Past signing in there are only a few: a resident's whole
 * relationship with the system is a lease, a rent figure, a payment history, a
 * way to say it is paid and a way to report a leak.
 */
export const PATHS = {
  signinOptions: 'GET /v1/signin-options',
  login: 'POST /v1/auth/tenant/login',
  google: 'POST /v1/auth/tenant/google',
  emailRequest: 'POST /v1/auth/tenant/email/request',
  emailVerify: 'POST /v1/auth/tenant/email/verify',
  phoneRequest: 'POST /v1/auth/tenant/phone/request',
  phoneVerify: 'POST /v1/auth/tenant/phone/verify',
  setPassword: 'POST /v1/auth/tenant/password',
  refresh: 'POST /v1/auth/refresh',
  logout: 'POST /v1/auth/logout',
  home: 'GET /v1/tenant/home',
  support: 'GET /v1/support',
  pay: 'POST /v1/tenant/pay',
  report: 'POST /v1/tenant/report',
} as const;

const BASE = import.meta.env.VITE_API_URL ?? '';

export type ErrorKind =
  | 'network' | 'badCode' | 'badLogin' | 'badEmail' | 'weakPassword' | 'expired' | 'googleFailed'
  | 'throttled' | 'noTenancy' | 'generic';

export class ApiError extends Error {
  constructor(readonly kind: ErrorKind, readonly status = 0, readonly code?: string) {
    super(kind);
    this.name = 'ApiError';
  }
}

let token: string | null = null;
export const setToken = (t: string | null): void => { token = t; };

/*
 * The access token lasts fifteen minutes; the refresh token buys another, once.
 * Without this the console signed everyone out four times an hour.
 */
let refreshToken: string | null = null;
let onRefreshed: ((fresh: { accessToken: string; refreshToken: string }) => void) | null = null;
/** This session's refresh token, and who to tell when it is spent and replaced. */
export const setRefresh = (
  r: string | null, cb: ((fresh: { accessToken: string; refreshToken: string }) => void) | null,
): void => { refreshToken = r; onRefreshed = cb; };

let inflight: Promise<boolean> | null = null;
/**
 * One refresh at a time. Requests that fail together share it: presenting a
 * spent refresh token again looks like theft, and ends the session.
 */
function refreshOnce(): Promise<boolean> {
  if (!refreshToken) return Promise.resolve(false);
  inflight ??= (async () => {
    try {
      const res = await fetch(BASE + '/v1/auth/refresh', {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return false;
      const fresh = (await res.json()) as { accessToken: string; refreshToken: string };
      token = fresh.accessToken;
      refreshToken = fresh.refreshToken;
      onRefreshed?.(fresh);
      return true;
    } catch {
      return false;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

async function request<T>(method: string, path: string, body?: unknown, lang?: Lang, retried = false): Promise<T> {
  let res: Response;
  try {
    res = await fetch(BASE + path, {
      method,
      headers: {
        'content-type': 'application/json',
        'x-burj-surface': 'tenant-web',
        // the resident's language: the sign-in code is texted in it, and signing in
        // stores it for the rent reminders and replies that follow
        ...(lang ? { 'accept-language': lang } : {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError('network');
  }

  if (res.status === 401 && !retried && !path.startsWith('/v1/auth/') && (await refreshOnce())) {
    return request<T>(method, path, body, lang, true);
  }
  if (res.status === 204) return undefined as T;

  const payload = await res.json().catch(() => ({}));
  const code = (payload as { error?: { code?: string } }).error?.code;
  if (res.status === 401 && !path.startsWith('/v1/auth/')) {
    // past signing in, a 401 is the end of the session
    window.dispatchEvent(new Event('burj:signed-out'));
    throw new ApiError('expired', 401, code);
  }
  if (!res.ok) {
    // Map to something a resident can act on. The API's own wording is written
    // for operators and is only ever in English.
    const kind: ErrorKind =
      code === 'invalid_credentials' ? 'badLogin'
      : code === 'code_invalid' ? 'badCode'
      : code === 'ticket_invalid' ? 'expired'
      : code === 'google_invalid' || code === 'google_off' ? 'googleFailed'
      : res.status === 404 ? 'noTenancy'
      // too_many_codes, code_locked: retrying straight away is refused again
      : res.status === 429 ? 'throttled'
      : res.status === 400 && path === '/v1/auth/tenant/password' ? 'weakPassword'
      : res.status === 400 && path.startsWith('/v1/auth/tenant/') ? 'badEmail'
      : 'generic';
    throw new ApiError(kind, res.status, code);
  }
  return payload as T;
}

export interface Lease {
  id: string; room: string; property: string; address: string;
  startDate: string; endDate: string; monthlyRent: number; deposit: number;
  rentPaused: boolean;
}

export interface Payment {
  id: string; amount: number; currency: string;
  received_on: string; method: string; kind: string;
}

export interface Home {
  lease: Lease;
  /** reported_paid: the resident told the office it is paid; not yet confirmed */
  nextRent: { id: string; due_date: string; amount: number; reported_paid?: boolean } | null;
  payments: Payment[];
  tenant?: { id: string; name: string } | null;
}

export type PayMethod = 'BankTransfer' | 'Cheque' | 'CreditCard' | 'Cash';

export interface SignedIn { accessToken: string; refreshToken?: string; tenant: { id: string; name: string } }
/** Not signed in yet: what to prove next, with a ticket to carry there. `phone` is masked. */
export interface NextStep { next: 'email' | 'phone' | 'password'; ticket: string; phone: string }
export type SignInResult = SignedIn | NextStep;
export const isSignedIn = (r: SignInResult): r is SignedIn => 'accessToken' in r;

export interface SignInOptions {
  google: { web?: string; ios?: string; android?: string } | null;
  minPasswordLength: number;
}

const tidy = (email: string): string => email.trim().toLowerCase();

export const api = {
  signinOptions: () => request<SignInOptions>('GET', '/v1/signin-options'),

  login: (email: string, password: string, lang: Lang) =>
    request<SignInResult>('POST', '/v1/auth/tenant/login', { email: tidy(email), password }, lang),

  google: (idToken: string, lang: Lang) =>
    request<SignInResult>('POST', '/v1/auth/tenant/google', { idToken }, lang),

  /** Creating an account and a forgotten password both start with a code to the email. */
  emailRequest: (email: string, lang: Lang) =>
    request<{ sent: true }>('POST', '/v1/auth/tenant/email/request', { email: tidy(email) }, lang),
  emailVerify: (email: string, code: string, lang: Lang) =>
    request<SignInResult>('POST', '/v1/auth/tenant/email/verify', { email: tidy(email), code }, lang),

  phoneRequest: (ticket: string, lang: Lang) =>
    request<{ sent: true; phone: string }>('POST', '/v1/auth/tenant/phone/request', { ticket }, lang),
  phoneVerify: (ticket: string, code: string, lang: Lang) =>
    request<SignInResult>('POST', '/v1/auth/tenant/phone/verify', { ticket, code }, lang),

  setPassword: (ticket: string, password: string, lang: Lang) =>
    request<SignInResult>('POST', '/v1/auth/tenant/password', { ticket, password }, lang),

  /** Ends the session's refresh family on the server. Best effort. */
  logout: (refreshToken: string) =>
    request<void>('POST', '/v1/auth/logout', { refreshToken }).catch(() => undefined),

  home: () => request<Home>('GET', '/v1/tenant/home'),
  /** The support phone, as set in the admin console; null until it is set. */
  support: () => request<{ phone: string | null }>('GET', '/v1/support'),

  report: (b: { category: string; description: string; emergency: boolean }, lang: Lang) =>
    request<{ reference: string; emergency: boolean }>('POST', '/v1/tenant/report', b, lang),

  /** A notice for the office, not a payment: nothing is charged, and the office confirms it. */
  pay: (b: { method: PayMethod; reference?: string }, lang: Lang) =>
    request<{ reported: true; alreadyReported: boolean; amount: number }>('POST', '/v1/tenant/pay', b, lang),
};

/** The categories a resident can pick. Kept in step with the i18n `category.*` keys. */
export const CATEGORIES = [
  'Plumbing', 'Electrical', 'AirConditioning', 'Appliance',
  'Internet', 'Lock', 'Cleaning', 'Pest', 'Other',
] as const;
